import React from "react";
import { FiDroplet, FiActivity, FiWifi, FiBattery, FiAlertTriangle, FiCheckCircle, FiThermometer, FiWind } from "react-icons/fi";
import { MdGrass, MdOutlineWaterDrop } from "react-icons/md";
import { KpiCard, HealthRing, zoneStatusColor } from "./irrigationHelpers";

const IrrOverviewPanel = ({ d }) => {
  const { summary, zones, site } = d;
  const maxWater = Math.max(...d.analytics.daily.map(x => x.water));

  return (
    <div className="irr-overview">

      {/* ── Top row: Health + Weather + AI ── */}
      <div className="irr-ov-top">
        <div className="irr-health-card">
          <HealthRing score={summary.healthScore} size={90} />
          <div>
            <div className="irr-health-title">System Health</div>
            <div className="irr-health-site">{site.name}</div>
            <div className="irr-health-loc">{site.location} · {site.totalArea} ha</div>
            <div className="irr-health-season">{site.season}</div>
          </div>
        </div>

        <div className="irr-weather-card">
          <div className="irr-weather-title">Weather Summary</div>
          <div className="irr-weather-main">{summary.weather.condition}</div>
          <div className="irr-weather-row">
            <span><FiThermometer size={13} /> {summary.weather.temp}°C</span>
            <span><MdOutlineWaterDrop size={13} /> {summary.weather.humidity}% RH</span>
            <span><FiWind size={13} /> {summary.weather.windSpeed} km/h</span>
          </div>
          <div className="irr-weather-rain">
            🌧 {summary.weather.rainChance}% chance of rain tomorrow
          </div>
          {summary.weather.rainChance > 10 && (
            <div className="irr-weather-warn">AI: Skip morning schedule to save water</div>
          )}
        </div>

        <div className="irr-ai-card">
          <div className="irr-ai-title">AI Insights</div>
          {summary.aiInsights.map((ins, i) => (
            <div key={i} className="irr-ai-row">
              <span className="irr-ai-dot" />
              {ins}
            </div>
          ))}
        </div>
      </div>

      {/* ── KPI Grid ── */}
      <div className="irr-kpi-grid">
        <KpiCard label="Total Zones"        value={summary.totalZones}       color="#667eea" icon={MdGrass}          sub={`${site.totalArea} hectares`} />
        <KpiCard label="Irrigating Now"     value={summary.activeZones}      color="#10b981" icon={FiDroplet}        sub="Active zones" />
        <KpiCard label="Water Used Today"   value={(summary.waterUsedToday / 1000).toFixed(1)} unit="kL" color="#3b82f6" icon={MdOutlineWaterDrop} sub={`${(summary.waterUsedThisWeek/1000).toFixed(0)} kL this week`} />
        <KpiCard label="Active Valves"      value={summary.activeValves}     color="#f97316" icon={FiActivity}       sub="Solenoid valves open" />
        <KpiCard label="Online LoRa Nodes"  value={summary.onlineNodes}      color="#8b5cf6" icon={FiWifi}           sub={`of ${d.loraNetwork.nodes.length} total`} />
        <KpiCard label="Low Battery Nodes"  value={summary.lowBatteryNodes}  color="#f59e0b" icon={FiBattery}        alert={summary.lowBatteryNodes > 0 ? "Replace batteries soon" : null} />
        <KpiCard label="Active Alerts"      value={summary.activeAlerts}     color="#ef4444" icon={FiAlertTriangle}  alert={summary.activeAlerts > 3 ? "Immediate attention needed" : null} />
        <KpiCard label="Avg Soil Moisture"  value={summary.avgSoilMoisture}  unit="%" color="#06b6d4" icon={FiThermometer} sub="Across all zones" />
        <KpiCard label="Scheduled Zones"    value={summary.scheduledZones}   color="#f59e0b" icon={FiCheckCircle}    sub="Pending execution" />
      </div>

      {/* ── Zone Status Summary ── */}
      <div className="irr-ov-bottom">
        <div className="irr-zone-status-card">
          <div className="irr-section-title">Zone Status Overview</div>
          <div className="irr-zone-status-legend">
            <span className="irr-zs-chip" style={{ background: "#d1fae5", color: "#10b981" }}>🟢 ON ({summary.activeZones})</span>
            <span className="irr-zs-chip" style={{ background: "#fef3c7", color: "#f59e0b" }}>🟡 Scheduled ({summary.scheduledZones})</span>
            <span className="irr-zs-chip" style={{ background: "#dbeafe", color: "#3b82f6" }}>🔵 Standby ({summary.standbyZones})</span>
            <span className="irr-zs-chip" style={{ background: "#fee2e2", color: "#ef4444" }}>🔴 Fault ({summary.faultZones})</span>
          </div>
          <div className="irr-zone-grid">
            {zones.map(z => {
              const st = zoneStatusColor(z.status);
              return (
                <div key={z.id} className="irr-zone-mini" style={{ borderColor: st.border, background: st.bg + "55" }}>
                  <div className="irr-zm-name">{z.id}</div>
                  <div className="irr-zm-crop">{z.name}</div>
                  <div className="irr-zm-status" style={{ color: st.color }}>{st.label}</div>
                  <div className="irr-zm-moisture">💧 {z.soilMoisture}%</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="irr-weekly-card">
          <div className="irr-section-title">Water Usage – Last 7 Days</div>
          <div className="irr-bar-chart">
            {d.analytics.daily.map(day => (
              <div key={day.day} className="irr-bar-col">
                <div className="irr-bar-wrap">
                  <div className="irr-bar-fill" style={{ height: `${(day.water / maxWater) * 100}%`, background: "linear-gradient(180deg, #667eea, #764ba2)" }} />
                </div>
                <div className="irr-bar-val">{(day.water / 1000).toFixed(0)}k</div>
                <div className="irr-bar-lbl">{day.day}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IrrOverviewPanel;
